import type { QRL } from "@builder.io/qwik";
import { component$, useSignal, $, useTask$ } from "@builder.io/qwik";
import { Input } from "./ui/input";
import { FileUpload } from "./ui/file-upload";
import { getSignedUrl, getPublicUrl } from "~/actions/upload-helpers";
import { Button } from "./ui/button";
import {
  LuX,
  LuFile,
  LuFileText,
  LuFileSpreadsheet,
  LuFileArchive,
  LuDownload,
} from "@qwikest/icons/lucide";

export const uploadFileToGCS = $(
  async (
    signedUrl: string,
    file: File,
    onProgress: (loaded: number, total: number) => void,
  ) => {
    return new Promise<void>((resolve, reject) => {
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", signedUrl);
      xhr.setRequestHeader("Content-Type", file.type);

      xhr.upload.addEventListener("progress", (event) => {
        if (event.lengthComputable) {
          onProgress(event.loaded, event.total);
        }
      });

      xhr.onreadystatechange = () => {
        if (xhr.readyState === 4) {
          if (xhr.status === 200) {
            resolve();
          } else {
            reject(new Error("Failed to upload file."));
          }
        }
      };

      xhr.onerror = () => {
        reject(new Error("An error occurred during the upload."));
      };

      xhr.send(file);
    });
  },
);

interface FileInputProps {
  name: string;
  type: "text" | "email" | "tel" | "password" | "url" | "date";
  label?: string;
  placeholder?: string;
  value: string | undefined | null;
  error?: unknown;
  required?: boolean;
  ref: QRL<(element: HTMLInputElement) => void>;
  onInput$: (event: Event, element: HTMLInputElement) => void;
  onChange$: QRL<(event: Event, element: HTMLInputElement) => void>;
  onBlur$: (event: Event, element: HTMLInputElement) => void;
  accept?: string;
  maxSize?: number;
  recommendedDimensions?: { width: number; height: number };
  uploadLabel?: string;
  previewType?: "image" | "none";
}

export const FileInput = component$<FileInputProps>(
  ({
    value = "",
    name,
    ref: setRef,
    onChange$,
    accept = "image/*",
    maxSize = 5 * 1024 * 1024, // 5MB default
    recommendedDimensions,
    uploadLabel,
    previewType = "image",
    ...rest
  }) => {
    const isUploading = useSignal<boolean>(false);
    const progress = useSignal<number>(0);
    const errorMessage = useSignal<string | null>(null);
    const previewUrl = useSignal<string>(value?.toString() ?? "");
    const fileName = useSignal<string>("");
    const fileType = useSignal<string>("");
    const ref = useSignal<HTMLInputElement>();

    const getFileIcon = $((type: string) => {
      if (
        type.includes("spreadsheet") ||
        type.includes("excel") ||
        type.includes("xlsx") ||
        type.includes("xls")
      ) {
        return <LuFileSpreadsheet class="h-6 w-6" />;
      }
      if (
        type.includes("pdf") ||
        type.includes("text") ||
        type.includes("doc")
      ) {
        return <LuFileText class="h-6 w-6" />;
      }
      if (
        type.includes("zip") ||
        type.includes("archive") ||
        type.includes("compressed")
      ) {
        return <LuFileArchive class="h-6 w-6" />;
      }
      return <LuFile class="h-6 w-6" />;
    });

    const getFileTypeLabel = $((type: string) => {
      if (
        type.includes("spreadsheet") ||
        type.includes("excel") ||
        type.includes("xlsx")
      )
        return "Spreadsheet";
      if (type.includes("pdf")) return "PDF Document";
      if (type.includes("doc")) return "Word Document";
      if (type.includes("zip") || type.includes("archive")) return "Archive";
      if (type.includes("text")) return "Text Document";
      return "File";
    });

    useTask$(({ track }) => {
      track(() => value);
      if (value) {
        previewUrl.value = value.toString();
        // Extract filename from URL
        const url = new URL(value);
        const pathSegments = url.pathname.split("/");
        fileName.value = decodeURIComponent(
          pathSegments[pathSegments.length - 1],
        );
      }
    });

    const handleFilesChange = $(async (files: File[]) => {
      if (files.length === 0) return;
      const file = files[0];

      isUploading.value = true;
      errorMessage.value = null;
      progress.value = 0;
      fileName.value = file.name;
      fileType.value = file.type;

      try {
        const { name: fileName, type } = file;
        const { signedUrl, blobName } = await getSignedUrl(fileName, type);

        await uploadFileToGCS(signedUrl, file, (loaded, total) => {
          progress.value = Math.round((loaded / total) * 100);
        });

        const publicUrl = await getPublicUrl(blobName);
        if (ref.value) {
          ref.value.value = publicUrl;
          previewUrl.value = publicUrl;

          // Dispatch change event to notify parent components
          const event = new Event("input", { bubbles: true });
          ref.value.dispatchEvent(event);
        }
      } catch (error) {
        console.error("Error uploading file:", error);
        errorMessage.value = "Failed to upload file.";
      } finally {
        isUploading.value = false;
      }
    });

    const handleClear = $(() => {
      if (ref.value) {
        ref.value.value = "";
        previewUrl.value = "";
        fileName.value = "";
        fileType.value = "";

        // Dispatch change event to notify parent components
        const event = new Event("input", { bubbles: true });
        ref.value.dispatchEvent(event);
      }
    });

    return (
      <div>
        {previewUrl.value && (
          <div class="mb-2 rounded border border-solid border-gray-200 p-2">
            <div class="mb-2 flex items-center justify-between">
              <Button
                onClick$={handleClear}
                variant="destructive-ghost"
                size="icon-sm"
                aria-label="Clear file"
              >
                <LuX class="h-4 w-4" />
              </Button>
            </div>
            {previewType === "image" ? (
              <img
                width="1024"
                height="1024"
                src={previewUrl.value}
                alt="Preview"
                class="max-h-[200px] w-full object-contain"
              />
            ) : (
              <div class="flex items-center gap-3 rounded bg-gray-50 p-3">
                <div class="text-brand-700">
                  {getFileIcon(fileType.value || fileName.value)}
                </div>
                <div class="flex-1 overflow-hidden">
                  <p
                    class="truncate text-sm font-medium"
                    title={fileName.value}
                  >
                    {fileName.value}
                  </p>
                  <p class="text-tertiary text-xs">
                    {getFileTypeLabel(fileType.value || fileName.value)}
                  </p>
                </div>
                <a
                  href={previewUrl.value}
                  target="_blank"
                  rel="noopener noreferrer"
                  class="text-brand-700 hover:text-brand-800"
                  aria-label="Download file"
                >
                  <LuDownload class="h-4 w-4" />
                </a>
              </div>
            )}
          </div>
        )}
        <Input
          name={name}
          value={value}
          placeholder="Enter file URL"
          class="mb-2"
          onChange$={onChange$}
          ref={$((x: HTMLInputElement) => {
            setRef(x);
            ref.value = x;
          })}
          {...rest}
          type="hidden"
        />
        <div class="relative">
          <FileUpload
            onChange$={handleFilesChange}
            isUploading={isUploading.value}
            progress={progress.value}
            accept={accept}
            maxSize={maxSize}
            recommendedDimensions={recommendedDimensions}
            label={uploadLabel}
          />
        </div>
        {errorMessage.value && (
          <p style={{ color: "red" }}>{errorMessage.value}</p>
        )}
      </div>
    );
  },
);

// For backward compatibility
export const ImageInput = FileInput;
